import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { CheckCircle2, Loader2, XCircle, Phone } from "lucide-react";

export default function OrderSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const orderId = params.get("order_id");
  const [status, setStatus] = useState("polling");
  const [details, setDetails] = useState(null);

  const poll = useCallback(async (attempt = 0) => {
    if (!sessionId) { setStatus("no_session"); return; }
    if (attempt >= 8) { setStatus("timeout"); return; }
    try {
      const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
      setDetails(data);
      if (data.payment_status === "paid") { setStatus("paid"); return; }
      if (data.status === "expired") { setStatus("expired"); return; }
      setTimeout(() => poll(attempt + 1), 2200);
    } catch (e) {
      setStatus("error");
    }
  }, [sessionId]);

  useEffect(() => { poll(); }, [poll]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="order-success-page">
      <div className="max-w-lg w-full bg-white border border-border p-10 offset-shadow">
        {status === "polling" && (
          <>
            <Loader2 className="animate-spin text-brand-red mb-4" size={40} />
            <h1 className="font-heading text-3xl mb-2">Confirming payment…</h1>
            <p className="text-muted-foreground">Hang tight while Stripe confirms your transaction.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="text-brand-red mb-4" size={48} />
            <h1 className="font-heading text-4xl mb-3" data-testid="success-heading">You're all set!</h1>
            <p className="text-muted-foreground mb-6">
              Payment of <span className="font-bold text-foreground">${((details?.amount_total ?? 0)/100).toFixed(2)}</span> received. Our dispatcher will call you within 2 hours to lock in a delivery window.
            </p>
            {orderId && <div className="bg-muted px-3 py-2 text-xs font-mono mb-6">Order ID · {orderId}</div>}
            <Link to="/" className="inline-block bg-brand-navy text-white px-6 py-3 font-bold hover:bg-brand-navyLight transition-colors" data-testid="success-home-btn">Back to home</Link>
          </>
        )}
        {(status === "expired" || status === "error" || status === "timeout" || status === "no_session") && (
          <>
            <XCircle className="text-brand-red mb-4" size={48} />
            <h1 className="font-heading text-3xl mb-2">Payment status unclear</h1>
            <p className="text-muted-foreground mb-6">We couldn't confirm the payment automatically. No charge may have been made. Please call us and we'll sort it out.</p>
            <a href="tel:6313723526" className="inline-flex items-center gap-2 bg-brand-red text-white px-6 py-3 font-bold" data-testid="success-call-btn"><Phone size={16} /> 631-372-3526</a>
          </>
        )}
      </div>
    </div>
  );
}
